import { EmptyState, Loading } from "../app/Status";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useBuildingContext } from "../lib/buildingContext";
import { useClientBuildingFilter } from "../lib/clientContext";
import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import {
  addDays,
  METER_FREQUENCIES,
  METER_FREQUENCY_LABELS,
  METER_ROUND_STATUS_LABELS,
  METER_TYPE_LABELS,
  periodOf,
  SIGNAL_LABELS,
  todayIso,
  type MeterCompareRow,
  type MeterFrequency,
  type MeterImportResult,
  type MeterRoundDetailDto,
  type MeterRoundDto,
  type MeterScheduleDto,
  type MeterSignalRow,
  type VacancyDto,
} from "@klantpunt/shared";
import {
  IconAlert,
  IconBuilding,
  IconDownload,
  IconFile,
  IconMeter,
  IconPlus,
  IconTrash,
  IconUpload,
} from "../app/icons";
import { Tabs } from "../app/Choice";
import { Modal } from "../app/Modal";
import { PageHeader } from "../app/PageHeader";
import { readExcelFile } from "../features/assets/AssetlogImport";
import { fmtDate, nl } from "../features/meters/MeterSection";
import { useRoundPdf } from "../features/meters/useRoundPdf";
import { api, ApiError } from "../lib/api";
import { hasModule, useMe } from "../lib/auth";
import { useToast } from "../lib/toast";

/**
 * Meterstanden (docs/METERSTANDEN.md): per gebouw de meters, het opnameschema, de rondes,
 * leegstand, Excel en import; daarnaast signalen (§6) en gebouwen vergelijken (§7).
 */
const errMsg = (e: unknown) => (e instanceof ApiError || e instanceof Error ? e.message : "Er ging iets mis");

interface BuildingRow {
  building: { id: string; code: string; name: string };
  meters: number;
  schedule: MeterScheduleDto | null;
  openRound: MeterRoundDto | null;
  lastRound: MeterRoundDto | null;
}

export function MetersPage() {
  const { data: me } = useMe();
  const [tab, setTab] = useState<"buildings" | "signals" | "compare">("buildings");
  if (!hasModule(me, "meters"))
    return (
      <div className="center">
        <p>Geen toegang tot de module Meterstanden.</p>
      </div>
    );
  const canEdit = hasModule(me, "meters", "edit");
  return (
    <div className="page meters">
      <PageHeader
        label="Opnemen per periode"
        title="Meterstanden"
        tabs={
          <Tabs
            label="Onderdeel"
            items={[
              { value: "buildings", label: "Gebouwen" },
              { value: "signals", label: "Signalen" },
              { value: "compare", label: "Vergelijken" },
            ]}
            value={tab}
            onChange={setTab}
          />
        }
      />
      {tab === "buildings" ? <Buildings canEdit={canEdit} /> : tab === "signals" ? <Signals /> : <Compare />}
    </div>
  );
}

const progress = (r: MeterRoundDto) => `${r.done} van ${r.total} opgenomen`;

function Buildings({ canEdit }: { canEdit: boolean }) {
  const q = useQuery({ queryKey: ["meter-buildings"], queryFn: () => api<BuildingRow[]>("/meters/buildings") });
  // Met een gekozen gebouw (bovenbalk) alleen dat gebouw, met de details open.
  const { id: current } = useBuildingContext();
  const [openRaw, setOpen] = useState<string | null>(null);
  const open = openRaw ?? current;
  const ofClient = useClientBuildingFilter();
  if (q.isPending) return <Loading />;
  const rows = current
    ? (q.data ?? []).filter((x) => x.building.id === current)
    : (q.data ?? []).filter((x) => ofClient(x.building.id));
  if (!rows.length) return <EmptyState icon={IconBuilding} title="Nog geen gebouwen" />;
  return (
    <section className="panel" aria-label="Gebouwen">
      <p className="small muted">
        Een meter is een asset van soort Meter. Voeg hem toe op de tekening van het gebouw en stel daar de soort en de
        telwerken in.
      </p>
      <ul className="list">
        {rows.map((x) => (
          <li key={x.building.id}>
            <div className="profile-row">
              <span className="grow">
                <strong>
                  {x.building.code} · {x.building.name}
                </strong>
                <span className="muted small block">
                  {x.meters} meter{x.meters === 1 ? "" : "s"}
                  {x.schedule?.active
                    ? ` · ${METER_FREQUENCY_LABELS[x.schedule.frequency].toLowerCase()}`
                    : " · geen schema"}
                  {x.openRound
                    ? ` · ${x.openRound.name}: ${progress(x.openRound)}`
                    : x.lastRound
                      ? ` · laatste ronde ${x.lastRound.name} (${METER_ROUND_STATUS_LABELS[x.lastRound.status].toLowerCase()})`
                      : ""}
                </span>
              </span>
              <span className="btnrow">
                {/* Uitgeklapt staat de lopende ronde met zijn knoppen eronder. */}
                {x.openRound && open !== x.building.id && (
                  <Link className="btn small primary" to={`/meterstanden/ronde/${x.openRound.id}`}>
                    {canEdit ? "Opnemen" : "Bekijken"}
                  </Link>
                )}
                <button
                  type="button"
                  className="btn small"
                  aria-expanded={open === x.building.id}
                  onClick={() => setOpen(open === x.building.id ? "" : x.building.id)}
                >
                  {open === x.building.id ? "Inklappen" : "Details"}
                </button>
              </span>
            </div>
            {open === x.building.id && <BuildingDetail row={x} canEdit={canEdit} />}
          </li>
        ))}
      </ul>
    </section>
  );
}

function BuildingDetail({ row, canEdit }: { row: BuildingRow; canEdit: boolean }) {
  const id = row.building.id;
  const rounds = useQuery({
    queryKey: ["meter-rounds", id],
    queryFn: () => api<MeterRoundDto[]>(`/buildings/${id}/meters/rounds`),
  });
  const pdf = useRoundPdf();
  const [dialog, setDialog] = useState<"round" | "schedule" | "vacancy" | "import" | null>(null);
  const to = todayIso();
  const [from, setFrom] = useState(addDays(to, -365));
  const [until, setUntil] = useState(to);
  const current = row.openRound;
  const earlier = (rounds.data ?? []).filter((r) => r.id !== current?.id);
  /** PDF en Excel van een ronde, naast elkaar. */
  const downloads = (r: MeterRoundDto) => (
    <span className="btnrow">
      <button type="button" className="btn small" disabled={pdf.busy === r.id} onClick={() => void pdf.make(r.id)}>
        <IconFile size={16} aria-hidden="true" />
        {pdf.busy === r.id ? "PDF maken…" : "PDF"}
      </button>
      <a className="btn small" href={`/api/meters/rounds/${r.id}/export.xlsx`} download>
        <IconDownload size={16} aria-hidden="true" />
        Excel
      </a>
    </span>
  );
  return (
    <div className="stack meterdetail">
      <p className="label">Lopende ronde</p>
      {current ? (
        <section className="meterround" aria-label={`Lopende ronde ${current.name}`}>
          <span className="grow">
            <strong>{current.name}</strong>
            <span className="dbar" aria-hidden="true">
              <span style={{ width: `${current.total ? Math.round((current.done / current.total) * 100) : 0}%` }} />
            </span>
            <span className="muted small">
              {fmtDate(current.periodStart)} t/m {fmtDate(current.periodEnd)} · {progress(current)}
              {current.flagged > 0 && ` · ${current.flagged} met afwijking`}
              {current.unreachable > 0 && ` · ${current.unreachable} niet bereikbaar`}
              {current.total > 0 && current.done >= current.total && " · klaar om af te ronden"}
            </span>
          </span>
          <span className="btnrow">
            <Link className="btn small primary" to={`/meterstanden/ronde/${current.id}`}>
              {canEdit
                ? current.done >= current.total
                  ? "Controleren en afronden"
                  : "Verder met opnemen"
                : "Bekijken"}
            </Link>
            {downloads(current)}
          </span>
        </section>
      ) : (
        <p className="muted small">
          Geen lopende ronde.{" "}
          {canEdit && "Stel een schema in, dan maakt de app elke periode een ronde, of maak er zelf een."}
        </p>
      )}
      <p className="label">Eerdere rondes</p>
      {rounds.isPending ? (
        <Loading small />
      ) : !earlier.length ? (
        <p className="muted small">Nog geen eerdere rondes.</p>
      ) : (
        <ul className="list compact roundlist">
          {earlier.map((r) => (
            <li key={r.id} className="row small">
              <span className="grow">
                <Link to={`/meterstanden/ronde/${r.id}`}>{r.name}</Link>
                <span className="muted block">
                  {METER_ROUND_STATUS_LABELS[r.status]} · {progress(r)}
                  {r.flagged > 0 && ` · ${r.flagged} met afwijking`}
                </span>
              </span>
              {downloads(r)}
            </li>
          ))}
        </ul>
      )}
      <fieldset className="row wrap">
        <legend className="label">Excel over een periode</legend>
        <label className="field">
          <span>Van</span>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="field">
          <span>Tot en met</span>
          <input type="date" value={until} onChange={(e) => setUntil(e.target.value)} />
        </label>
        <a className="btn small" href={`/api/buildings/${id}/meters/export.xlsx?from=${from}&to=${until}`} download>
          <IconDownload size={16} aria-hidden="true" />
          Excel
        </a>
      </fieldset>
      {canEdit && (
        <>
          <p className="label">Instellen</p>
          <div className="btnrow">
            <button type="button" className="btn small" onClick={() => setDialog("round")}>
              <IconPlus size={16} aria-hidden="true" />
              Nieuwe ronde
            </button>
            <button type="button" className="btn small" onClick={() => setDialog("schedule")}>
              Schema
            </button>
            <button type="button" className="btn small" onClick={() => setDialog("vacancy")}>
              Leegstand gebouw
            </button>
            <button type="button" className="btn small" onClick={() => setDialog("import")}>
              <IconUpload size={16} aria-hidden="true" />
              Eerdere standen importeren
            </button>
          </div>
        </>
      )}
      <BuildingVacancies buildingId={id} canEdit={canEdit} />
      {dialog === "round" && <RoundDialog buildingId={id} schedule={row.schedule} onClose={() => setDialog(null)} />}
      {dialog === "schedule" && (
        <ScheduleDialog buildingId={id} schedule={row.schedule} onClose={() => setDialog(null)} />
      )}
      {dialog === "vacancy" && <VacancyDialog buildingId={id} onClose={() => setDialog(null)} />}
      {dialog === "import" && <ImportDialog buildingId={id} onClose={() => setDialog(null)} />}
    </div>
  );
}

function BuildingVacancies({ buildingId, canEdit }: { buildingId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const key = ["meter-vacancies", buildingId];
  const q = useQuery({ queryKey: key, queryFn: () => api<VacancyDto[]>(`/buildings/${buildingId}/meters/vacancies`) });
  const list = q.data?.filter((v) => !v.assetId) ?? [];
  if (!list.length) return null;
  const run = async (fn: () => Promise<VacancyDto[]>) => {
    try {
      qc.setQueryData(key, await fn());
    } catch (e) {
      toast(errMsg(e));
    }
  };
  return (
    <>
      <p className="label">Leegstand gebouw</p>
      <ul className="list compact">
        {list.map((v) => (
          <li key={v.id} className="row small">
            <span className="grow">
              Leeg vanaf {fmtDate(v.startsOn)}
              {v.endsOn ? ` t/m ${fmtDate(v.endsOn)}` : " (nog leeg)"}
              {v.note && <span className="muted"> · {v.note}</span>}
            </span>
            {canEdit && !v.endsOn && (
              <button
                type="button"
                className="btn small ghost"
                onClick={() =>
                  void run(() =>
                    api<VacancyDto[]>(`/meters/vacancies/${v.id}`, { method: "PATCH", body: { endsOn: todayIso() } }),
                  )
                }
              >
                Beëindigen
              </button>
            )}
            {canEdit && (
              <button
                type="button"
                className="btn ico ghost small"
                aria-label="Leegstand verwijderen"
                onClick={() => void run(() => api<VacancyDto[]>(`/meters/vacancies/${v.id}`, { method: "DELETE" }))}
              >
                <IconTrash size={16} aria-hidden="true" />
              </button>
            )}
          </li>
        ))}
      </ul>
    </>
  );
}

function RoundDialog({
  buildingId,
  schedule,
  onClose,
}: {
  buildingId: string;
  schedule: MeterScheduleDto | null;
  onClose: () => void;
}) {
  const toast = useToast();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const p = periodOf(schedule?.frequency ?? "monthly", todayIso());
  const [start, setStart] = useState(p.start);
  const [end, setEnd] = useState(p.end);
  const save = useMutation({
    mutationFn: () =>
      api<MeterRoundDetailDto>(`/buildings/${buildingId}/meters/rounds`, {
        method: "POST",
        body: { id: crypto.randomUUID(), periodStart: start, periodEnd: end },
      }),
    onSuccess: (d) => {
      void qc.invalidateQueries({ queryKey: ["meter-buildings"] });
      void qc.invalidateQueries({ queryKey: ["meter-rounds", buildingId] });
      navigate(`/meterstanden/ronde/${d.round.id}`);
    },
    onError: (e) => toast(errMsg(e)),
  });
  return (
    <Modal
      title="Nieuwe opnameronde"
      onClose={onClose}
      busy={save.isPending}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Annuleren
          </button>
          <button type="submit" form="roundform" className="btn primary" disabled={save.isPending}>
            Ronde maken
          </button>
        </>
      }
    >
      <form
        id="roundform"
        className="stack"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <p className="small muted">Alle actieve meters van het gebouw komen in de ronde.</p>
        <div className="grid2">
          <label className="field">
            <span>Periode van</span>
            <input type="date" value={start} onChange={(e) => setStart(e.target.value)} required />
          </label>
          <label className="field">
            <span>Tot en met</span>
            <input type="date" value={end} min={start} onChange={(e) => setEnd(e.target.value)} required />
          </label>
        </div>
      </form>
    </Modal>
  );
}

/** Melding na opslaan: wanneer de taak van de eerstvolgende periode verschijnt. */
function scheduleSavedText(frequency: MeterFrequency, startsOn: string, reminderDays: number, active: boolean) {
  if (!active) return "Schema opgeslagen; staat uit, er komen geen rondes of taken";
  const today = todayIso();
  const p = periodOf(frequency, startsOn > today ? startsOn : today);
  const taskOn = addDays(p.end, -reminderDays);
  return taskOn <= today
    ? "Schema opgeslagen. De taak Meterstanden opnemen verschijnt vandaag"
    : `Schema opgeslagen. De taak Meterstanden opnemen verschijnt op ${fmtDate(taskOn)}`;
}

function ScheduleDialog({
  buildingId,
  schedule,
  onClose,
}: {
  buildingId: string;
  schedule: MeterScheduleDto | null;
  onClose: () => void;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const [frequency, setFrequency] = useState<MeterFrequency>(schedule?.frequency ?? "monthly");
  const [startsOn, setStartsOn] = useState(schedule?.startsOn ?? periodOf("monthly", todayIso()).start);
  const [reminder, setReminder] = useState(String(schedule?.reminderDays ?? 7));
  const [active, setActive] = useState(schedule?.active ?? true);
  const save = useMutation({
    mutationFn: () =>
      api(`/buildings/${buildingId}/meters/schedule`, {
        method: "PUT",
        body: { frequency, startsOn, reminderDays: Number(reminder) || 0, active },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["meter-buildings"] });
      void qc.invalidateQueries({ queryKey: ["meter-rounds", buildingId] });
      toast(scheduleSavedText(frequency, startsOn, Number(reminder) || 0, active));
      onClose();
    },
    onError: (e) => toast(errMsg(e)),
  });
  return (
    <Modal
      title="Opnameschema"
      onClose={onClose}
      busy={save.isPending}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Annuleren
          </button>
          <button type="submit" form="scheduleform" className="btn primary" disabled={save.isPending}>
            Opslaan
          </button>
        </>
      }
    >
      <form
        id="scheduleform"
        className="stack"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <p className="small muted">
          De app maakt elke periode een opnameronde met alle actieve meters, en een taak <em>Meterstanden opnemen</em>{" "}
          een aantal dagen voor het einde van de periode.
        </p>
        <div className="grid2">
          <label className="field">
            <span>Frequentie</span>
            <select value={frequency} onChange={(e) => setFrequency(e.target.value as MeterFrequency)}>
              {METER_FREQUENCIES.map((f) => (
                <option key={f} value={f}>
                  {METER_FREQUENCY_LABELS[f]}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Vanaf</span>
            <input type="date" value={startsOn} onChange={(e) => setStartsOn(e.target.value)} required />
          </label>
          <label className="field">
            <span>Taak … dagen voor het einde</span>
            <input
              inputMode="numeric"
              value={reminder}
              onChange={(e) => setReminder(e.target.value.replace(/\D/g, ""))}
            />
          </label>
        </div>
        <label className="check">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          Actief
        </label>
      </form>
    </Modal>
  );
}

function VacancyDialog({ buildingId, onClose }: { buildingId: string; onClose: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [from, setFrom] = useState(todayIso());
  const [to, setTo] = useState("");
  const [note, setNote] = useState("");
  const save = useMutation({
    mutationFn: () =>
      api<VacancyDto[]>(`/buildings/${buildingId}/meters/vacancies`, {
        method: "POST",
        body: { id: crypto.randomUUID(), startsOn: from, endsOn: to || null, note: note || null },
      }),
    onSuccess: (d) => {
      qc.setQueryData(["meter-vacancies", buildingId], d);
      toast("Leegstand vastgelegd");
      onClose();
    },
    onError: (e) => toast(errMsg(e)),
  });
  return (
    <Modal
      title="Leegstand gebouw"
      onClose={onClose}
      busy={save.isPending}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Annuleren
          </button>
          <button type="submit" form="bvacancyform" className="btn primary" disabled={save.isPending}>
            Opslaan
          </button>
        </>
      }
    >
      <form
        id="bvacancyform"
        className="stack"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <p className="small muted">
          Het hele gebouw staat leeg. Verbruik boven de basislast van een meter geeft dan een signaal. Leegstand van één
          unit leg je vast bij de tussenmeter.
        </p>
        <div className="grid2">
          <label className="field">
            <span>Leeg vanaf</span>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} required />
          </label>
          <label className="field">
            <span>Tot en met (leeg = nog leeg)</span>
            <input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
          </label>
        </div>
        <label className="field">
          <span>Opmerking</span>
          <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} />
        </label>
      </form>
    </Modal>
  );
}

function ImportDialog({ buildingId, onClose }: { buildingId: string; onClose: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<MeterImportResult | null>(null);
  async function pick(f: File | undefined) {
    if (!f) return;
    setBusy(true);
    try {
      const r = await api<MeterImportResult>(`/buildings/${buildingId}/meters/import`, {
        method: "POST",
        body: { file: await readExcelFile(f) },
      });
      setResult(r);
      void qc.invalidateQueries({ queryKey: ["meter-buildings"] });
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal title="Eerdere standen importeren" onClose={onClose} busy={busy}>
      <div className="stack">
        <p className="small">
          Een Excel-bestand met de kolommen <strong>Meter</strong> (assetcode, EAN of meternummer),{" "}
          <strong>Telwerk</strong> (leeg = het eerste), <strong>Datum</strong> en <strong>Stand</strong>. Zo werkt de
          vergelijking met vorig jaar vanaf de eerste ronde. Een opname op een datum die er al is, wordt overgeslagen.
        </p>
        <label className="btn primary">
          <IconUpload size={18} aria-hidden="true" />
          {busy ? "Bezig…" : "Kies een bestand"}
          <input
            type="file"
            accept=".xlsx"
            hidden
            disabled={busy}
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              void pick(f);
            }}
          />
        </label>
        {result && (
          <div role="status" className="small">
            <p>
              {result.imported} opname{result.imported === 1 ? "" : "s"} geïmporteerd
              {result.skipped > 0 && `, ${result.skipped} overgeslagen (bestond al)`}.
            </p>
            {result.errors.length > 0 && (
              <ul className="list compact">
                {result.errors.slice(0, 20).map((x) => (
                  <li key={x.row} className="note warn">
                    Rij {x.row}: {x.message}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

function Signals() {
  const q = useQuery({ queryKey: ["meter-signals"], queryFn: () => api<MeterSignalRow[]>("/meters/signals") });
  if (q.isPending) return <Loading />;
  if (!q.data?.length)
    return (
      <section className="panel">
        <p className="muted">
          Geen signalen. De app kijkt na elke opname naar sprongen, sluipende stijging, verbruik bij leegstand en
          stilstaande meters.
        </p>
      </section>
    );
  return (
    <section className="panel" aria-label="Signalen">
      <ul className="list">
        {q.data.map((s) => (
          <li key={`${s.asset.id}-${s.code}`} className="profile-row">
            <IconAlert size={20} aria-hidden="true" />
            <span className="grow">
              <strong>{SIGNAL_LABELS[s.code]}</strong> · {s.building.code} · {s.asset.code} {s.asset.name}
              <span className="muted small block">
                {METER_TYPE_LABELS[s.meterType]} · tot {fmtDate(s.to)} · {s.message}
              </span>
            </span>
            <Link className="btn small" to={`/gebouwen/${s.building.id}?asset=${s.asset.id}`}>
              Meter
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Compare() {
  const to0 = todayIso();
  const [from, setFrom] = useState(addDays(to0, -365));
  const [to, setTo] = useState(to0);
  const q = useQuery({
    queryKey: ["meter-compare", from, to],
    queryFn: () => api<MeterCompareRow[]>(`/meters/compare?from=${from}&to=${to}`),
  });
  const rows = [...(q.data ?? [])].sort(
    (a, b) => a.meterType.localeCompare(b.meterType) || (b.perDay ?? -1) - (a.perDay ?? -1),
  );
  return (
    <section className="panel stack" aria-label="Vergelijken">
      <div className="row wrap">
        <label className="field">
          <span>Van</span>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="field">
          <span>Tot en met</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
      </div>
      <p className="small muted">
        Verbruik per dag van de hoofdmeters (tussenmeters zitten daarin), over perioden die helemaal in de gekozen
        periode vallen. Het verschil met een jaar eerder telt alleen perioden die in beide jaren zijn opgenomen, zodat
        bijvoorbeeld een winter niet met een zomer wordt vergeleken.
      </p>
      {q.isPending ? (
        <Loading />
      ) : !rows.length ? (
        <EmptyState icon={IconMeter} title="Nog geen verbruik" text="In deze periode zijn geen standen opgenomen." />
      ) : (
        <div className="tscroll">
          <table className="it metercompare">
            <thead>
              <tr>
                <th scope="col">Gebouw</th>
                <th scope="col">Soort</th>
                <th scope="col" className="num">
                  Meters
                </th>
                <th scope="col" className="num">
                  Verbruik
                </th>
                <th scope="col" className="num">
                  Per dag
                </th>
                <th scope="col" className="num">
                  Jaar eerder per dag
                </th>
                <th scope="col" className="num">
                  Verschil
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.building.id}-${r.meterType}`}>
                  <td>
                    {r.building.code} · {r.building.name}
                  </td>
                  <td>{METER_TYPE_LABELS[r.meterType]}</td>
                  <td className="num">{r.meters}</td>
                  <td className="num">
                    {nl(r.usage, 0)} {r.unit}
                  </td>
                  <td className="num">
                    {nl(r.perDay, 2)} {r.unit}
                  </td>
                  <td className="num">{r.lastYearPerDay != null ? `${nl(r.lastYearPerDay, 2)} ${r.unit}` : "—"}</td>
                  <td className="num">
                    {r.comparedPerDay != null && r.lastYearPerDay
                      ? `${r.comparedPerDay >= r.lastYearPerDay ? "+" : ""}${Math.round((r.comparedPerDay / r.lastYearPerDay - 1) * 100)}%`
                      : "—"}
                    {r.comparedDays > 0 && r.comparedDays < r.days && (
                      <span className="muted small block">over {r.comparedDays} dagen</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
